/**
 * Edit entity — /settings/custom-modules/$key/entities/$entityKey
 *
 * Loads the entity, pre-fills the form, and on save calls
 * modules.custom.entities.update. Also includes a Delete button that
 * cascades through all records of this entity.
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import type { EntityScenarios } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { EntityPreviewPanel } from '@/components/custom/entity-preview-panel'
import { FieldsEditor } from '@/components/custom/fields-editor'
import { ScenariosEditor } from '@/components/custom/scenarios-editor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/entities/$entityKey')({
  component: EditEntityPage,
})

function EditEntityPage() {
  const { key: moduleKey, entityKey } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: entity, isLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const [name, setName] = useState('')
  const [namePlural, setNamePlural] = useState('')
  const [description, setDescription] = useState('')
  const [fields, setFields] = useState<FieldDefinition[]>([])
  const [primaryDisplayField, setPrimaryDisplayField] = useState('')
  const [scenarios, setScenarios] = useState<EntityScenarios | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  useEffect(() => {
    if (entity) {
      setName(entity.name)
      setNamePlural(entity.namePlural ?? '')
      setDescription(entity.description ?? '')
      setFields(entity.fields as FieldDefinition[])
      setPrimaryDisplayField(entity.primaryDisplayField ?? '')
      setScenarios((entity.scenarios as EntityScenarios | null) ?? null)
    }
  }, [entity])

  const updateMutation = useMutation(
    orpc.modules.custom.entities.update.mutationOptions({
      onSuccess: () => {
        toast.success('Entity updated')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.entities.get.queryKey({
            input: { moduleKey, key: entityKey },
          }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.entities.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => setFormError(err.message || 'Failed to update entity'),
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.entities.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Entity deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.entities.list.queryKey({
            input: { moduleKey },
          }),
        })
        void navigate({
          to: '/settings/custom-modules/$key/entities',
          params: { key: moduleKey },
        })
      },
      onError: (err) => toast.error(err.message || 'Failed to delete entity'),
    }),
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!entity) {
    return <p className="text-muted-foreground">Entity not found.</p>
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    updateMutation.mutate({
      moduleKey,
      key: entityKey,
      name,
      namePlural: namePlural || undefined,
      description: description || undefined,
      fields,
      primaryDisplayField: primaryDisplayField || undefined,
      scenarios,
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link
          to="/settings/custom-modules/$key/entities"
          params={{ key: moduleKey }}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to entities
        </Link>
        <Link
          to="/settings/extensions/$moduleKey/$entityKey/layout"
          params={{ moduleKey, entityKey }}
          className="inline-flex items-center text-sm text-primary hover:underline"
        >
          Edit layout JSON →
        </Link>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Edit {entity.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="namePlural">Plural name</Label>
                <Input
                  id="namePlural"
                  value={namePlural}
                  onChange={(e) => setNamePlural(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="key-display">Key</Label>
                <Input id="key-display" value={entity.key} disabled />
                <p className="text-xs text-muted-foreground mt-1">Immutable once created.</p>
              </div>
              <div>
                <Label htmlFor="primary">Primary display field</Label>
                <Select
                  value={primaryDisplayField || '__none__'}
                  onValueChange={(v) => setPrimaryDisplayField(v === '__none__' ? '' : v)}
                >
                  <SelectTrigger id="primary">
                    <SelectValue placeholder="— auto —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— auto —</SelectItem>
                    {fields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {f.label || f.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldsEditor fields={fields} onChange={setFields} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Scenarios</CardTitle>
          </CardHeader>
          <CardContent>
            <ScenariosEditor value={scenarios} onChange={setScenarios} />
          </CardContent>
        </Card>

        <EntityPreviewPanel
          moduleKey={moduleKey}
          entityKey={entityKey}
          fields={fields}
          scenarios={scenarios}
        />

        {formError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {formError}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
            className="mr-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete entity
          </Button>
          <Link to="/settings/custom-modules/$key/entities" params={{ key: moduleKey }}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{entity.name}"?</DialogTitle>
            <DialogDescription>
              This permanently deletes the entity and ALL its records. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate({ moduleKey, key: entityKey })}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
